/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import Loader from "utils/Loader";
import WindowedSelect from "react-windowed-select";
import Select from 'react-select';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Divider,
  Grow,
  Stack,
  Modal,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InventoryIcon from "@mui/icons-material/Inventory2";
import ReceiptIcon from "@mui/icons-material/ReceiptLong";
import ShippingIcon from "@mui/icons-material/LocalShipping";
import TargetIcon from "@mui/icons-material/TrackChanges";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { CurrencyRupee } from "../../../node_modules/@mui/icons-material/index";
import { OpenWith } from "../../../node_modules/@mui/icons-material/index";

import UserReportsController from "../Controller/UserReportsController";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const palette = [
  "#1e88e5",
  "#00bfa5",
  "#00bcd4",
  "#8e9aa8",
  "#6b7280",
  "#3b82f6",
  "#fb923c",
];

const selectStyles = (theme) => ({
  control: (provided, state) => ({
    ...provided,
    background: "#f7f8fa",
    borderRadius: 8,
    minHeight: 38,
    height: 38,
    paddingLeft: 4,
    paddingRight: 4,
    borderColor: state.isFocused ? theme.palette.primary.main : "#d4d6db",
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.palette.primary.main}55` : "none",
    transition: "all 0.15s ease",
    fontSize: 13.5,
    color: "#2c3136",
  }),
  valueContainer: (p) => ({ ...p, padding: "0 6px" }),
  singleValue: (p) => ({ ...p, color: "#2c3136", fontWeight: 500 }),
  placeholder: (p) => ({ ...p, fontSize: 13, color: "#9aa0a6" }),
  option: (p, state) => ({
    ...p,
    padding: "8px 12px",
    background: state.isSelected ? theme.palette.primary.light + "33" : state.isFocused ? "#eef1f5" : "#ffffff",
    color: "#2b3137",
    fontSize: 13.5,
    cursor: "pointer",
  }),
  menu: (p) => ({ ...p, background: "#ffffff", borderRadius: 8, marginTop: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", zIndex: 1500 }),
  menuList: (p) => ({ ...p, paddingTop: 4, paddingBottom: 4 }),
  indicatorsContainer: (p) => ({ ...p, padding: "0 4px" }),
  dropdownIndicator: (p) => ({ ...p, padding: 4 }),
  clearIndicator: (p) => ({ ...p, padding: 4 }),
});

const safeSum = (arr = []) => arr.reduce((s, v) => s + (Number(v) || 0), 0);

const CardHeader = ({ title, subtitle, Icon, chartKey, onMaximize }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Box display="flex" alignItems="center" gap={1}>
        {Icon && <Icon fontSize="small" color="primary" />}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        {/* Maximize button opens modal for this chart */}
        {onMaximize && (
          <Tooltip title="Maximize" placement="top">
            <IconButton size="small" onClick={() => onMaximize(chartKey)} sx={{ backgroundColor: "rgba(0,91,209,0.08)", borderRadius: "10px" }}>
              <OpenWith sx={{ color: "#1890ff", fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};


/* -------------------------
   Main component
   ------------------------- */
const UserReports = () => {
  const theme = useTheme();
  const {
    cloading, loading, 
    parties,
    selectedParty,
    setSelectedParty,
    // Reports Data
    targetAchievementReportData,
    collectionsReportData,
    pendingDispatchReportData,
    outstandingReportData,
    salesReportData,
    stockLevelReportData,

    salesChartTypes,
    selectedSalesChartType,
    setSelectedSalesChartType,
  } = UserReportsController();

  const user = JSON.parse(localStorage.getItem("user")) ?? null;

  const [filters, setFilters] = useState({ reportType: null, category: null, user: null, party: null });
  const [applyTick, setApplyTick] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [activeChartKey, setActiveChartKey] = useState(null);

  // canvas refs (one per chart)
  const salesRef = useRef(null);
  const stockRef = useRef(null);
  const outRef = useRef(null);
  const pendRef = useRef(null);
  const targetRef = useRef(null);
  const collRef = useRef(null);

  // store chart instances
  const charts = useRef({});

  const destroyCharts = (keys = null) => {
    if (!keys) {
      Object.values(charts.current).forEach((c) => { try { c.destroy(); } catch { } });
      charts.current = {};
      return;
    }
    keys.forEach((k) => {
      if (charts.current[k]) {
        try { charts.current[k].destroy(); } catch { }
        delete charts.current[k];
      }
    });
  };


  useEffect(() => {
    if (!salesReportData) return;
    destroyCharts(["sales"]);

    try {
      const ctx = salesRef.current.getContext("2d");

      // 1. dynamic month order
      const monthsOrder = Object.keys(salesReportData);
      // 2. Detect all product categories dynamically
      const productTypes = new Set();
      monthsOrder.forEach(m => {
        const monthData = salesReportData?.[m] || {};
        Object.keys(monthData).forEach(k => productTypes.add(k));
      });

      // 3. Build datasets
      const datasets = [...productTypes].map((type, i) => {
        const color = palette[i % palette.length];

        const grad = (() => {
          const g = ctx.createLinearGradient(0, 0, 0, 300);
          g.addColorStop(0, color);
          g.addColorStop(1, "rgba(255,255,255,0.05)");
          return g;
        })();

        return {
          label: type,
          data: monthsOrder.map(m =>
            Number(salesReportData?.[m]?.[type] || 0)
          ),
          borderColor: color,
          backgroundColor: grad,
          tension: 0.25,
          fill: true,
          pointRadius: 3,
          _solidColor: color,
        };
      });

      // 4. Clean Y axis
      const allVals = datasets.flatMap(d => d.data);
      const rawMax = Math.max(...allVals, 0);

      let niceMax;
      if (rawMax === 0) {
        niceMax = 100;
      } else {
        const maxWithMargin = Math.ceil(rawMax + rawMax * 0.1);
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxWithMargin)));
        niceMax = Math.ceil(maxWithMargin / magnitude) * magnitude;
      }

      // 5. Draw Chart
      charts.current.sales = new Chart(ctx, {
        type: "line",
        data: { labels: monthsOrder, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
              align: "end",
              labels: { boxWidth: 10 }
            },
            tooltip: {
              mode: "index",
              intersect: false
            }
          },
          interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false
          },
          scales: {
            y: {
              beginAtZero: true,
              max: niceMax,
              ticks: {
                callback: (val) => val.toLocaleString("en-IN")
              }
            }
          }
        }
      });

    } catch (e) {
      console.error("Sales chart error", e);
    }
  }, [salesReportData, applyTick]);


  /* -------------------------
     Stock (was doughnut) -> now vertical bar
     ------------------------- */
  useEffect(() => {
    if (!stockLevelReportData) return;

    destroyCharts(["stock"]);

    try {
      const ctx = stockRef.current.getContext("2d");

      // Convert object into labels + values
      const labels = Object.keys(stockLevelReportData || {});
      const vals = Object.values(stockLevelReportData || []).map(v =>
        Number((v + "").replace(/,/g, "")) || 0
      );

      // Compute y-axis ticks
      const maxVal = vals.length ? Math.max(...vals) : 0;
      const desiredSteps = 6;
      const stepSize = Math.ceil((maxVal || 1) / desiredSteps) || 1;
      const suggestedMax = stepSize * desiredSteps;

      charts.current.stock = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Stock",
              data: vals,
              backgroundColor: palette.map(c => c + "CC"),
              borderColor: palette,
              borderWidth: 1,
              maxBarThickness: 40,
              _solidColor: palette.map(c => c + "CC"),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { mode: "index", intersect: false }
          },
          scales: {
            x: { stacked: false, grid: { display: false } },
            y: {
              beginAtZero: true,
              suggestedMax,
              ticks: { stepSize },
            },
          },
        },
      });
    } catch (e) {
      console.error("Stock (bar) chart error", e);
    }

  }, [stockLevelReportData, applyTick]);


  /* -------------------------
   Outstanding (bar)
   ------------------------- */
  useEffect(() => {
    if (!outstandingReportData) return;
    destroyCharts(["outstanding"]);

    try {
      const ctx = outRef.current.getContext("2d");

      // 1. Extract labels & values
      const labels = outstandingReportData.partyOutstanding.map(
        item => item.daySlot
      );

      const values = outstandingReportData.partyOutstanding.map(
        item => Number(String(item.billAmount).replace(/,/g, "")) || 0
      );

      // 2. Dynamic Y-axis (clean)
      const rawMax = Math.max(...values, 0);
      let niceMax;

      if (rawMax === 0) {
        niceMax = 100; // fallback clean scale
      } else {
        const maxWithMargin = Math.ceil(rawMax + rawMax * 0.1); // +10% margin
        const magnitude = Math.pow(
          10,
          Math.floor(Math.log10(maxWithMargin))
        );
        niceMax = Math.ceil(maxWithMargin / magnitude) * magnitude;
      }

      // 3. Draw chart
      charts.current.outstanding = new Chart(ctx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: [
            {
              label: "Outstanding",
              data: values,
              backgroundColor: palette[7]
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          scales: {
            x: {
              title: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              max: niceMax,
              ticks: {
                callback: (value) => value.toLocaleString("en-IN")
              },
              title: {
                display: true,
                text: "Outstanding Amount"
              }
            }
          }
        }
      });

    } catch (e) {
      console.error("Outstanding chart error", e);
    }
  }, [outstandingReportData, applyTick]);


  /* -------------------------
   Pending vs Dispatch (bar)
   ------------------------- */
  useEffect(() => {
    if (!pendingDispatchReportData) return;
    destroyCharts(["pending"]);

    try {
      const ctx = pendRef.current.getContext("2d");

      // 1. fixed month order
      // const monthsOrder = [
      //   "Apr", "May", "Jun", "Jul",
      //   "Aug", "Sep", "Oct", "Nov",
      //   "Dec", "Jan", "Feb", "Mar"
      // ];

      // ✔ 1. Dynamic months from API
      const monthsOrder = Object.keys(pendingDispatchReportData);
      // 2. Extract pending and dispatch arrays from object
      const pendingVals = monthsOrder.map(
        m => Number(pendingDispatchReportData?.[m]?.ordered ?? 0)
      );

      const dispatchVals = monthsOrder.map(
        m => Number(pendingDispatchReportData?.[m]?.dispatch ?? 0)
      );

      // 3. Dynamic scale calculation (shared logic)
      const all = [...pendingVals, ...dispatchVals];
      const rawMax = Math.max(...all, 0);

      let niceMax;

      if (rawMax === 0) {
        niceMax = 100;        // Safe fallback
      } else {
        const maxWithMargin = Math.ceil(rawMax + rawMax * 0.1);   // +10%
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxWithMargin)));
        niceMax = Math.ceil(maxWithMargin / magnitude) * magnitude;
      }

      // 4. Draw chart
      charts.current.pending = new Chart(ctx, {
        type: "bar",
        data: {
          labels: monthsOrder,
          datasets: [
            {
              label: "Ordered",
              data: pendingVals,
              backgroundColor: palette[6]
            },
            {
              label: "Dispatch",
              data: dispatchVals,
              backgroundColor: palette[1]
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "top", align: "end" }
          },
          scales: {
            x: {
              title: { display: false }
            },
            y: {
              beginAtZero: true,
              max: niceMax,
              ticks: {
                callback: (val) => val.toLocaleString("en-IN")
              },
              title: {
                display: true,
                text: "Order Quantity"
              }
            }
          }
        }
      });

    } catch (e) {
      console.error("Pending chart error", e);
    }
  }, [pendingDispatchReportData, applyTick]);


  /* -------------------------
     Target vs Achievement (bar)
     ------------------------- */

  useEffect(() => {
    if (!targetAchievementReportData) return;
    destroyCharts(["target"]);

    try {
      const ctx = targetRef.current.getContext("2d");

      const targetData = targetAchievementReportData.target || {};
      const achievementData = targetAchievementReportData.achievement || {};

      // Get months exactly as returned by API
      const monthsOrder = Object.keys(targetData);

      // 2. Convert object data → arrays
      const t = monthsOrder.map(m => targetAchievementReportData.target?.[m] ?? 0);
      const a = monthsOrder.map(m => targetAchievementReportData.achievement?.[m] ?? 0);

      // 3. Dynamic scale calculation
      const all = [...t, ...a].map((v) => Number(v) || 0);
      const maxVal = all.length ? Math.ceil(Math.max(...all) + (Math.max(...all) * 0.1)) : 0;

      // Clean rounding
      const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
      const niceMax = Math.ceil(maxVal / magnitude) * magnitude;
      // const niceMax = 100;

      // 4. Draw chart
      charts.current.target = new Chart(ctx, {
        type: "bar",
        data: {
          labels: monthsOrder,   // ⬅ updated
          datasets: [
            { label: "Target", data: t, backgroundColor: palette[0] },
            { label: "Achievement", data: a, backgroundColor: palette[1] }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "top", align: "end" } },
          scales: {
            x: {
              title: {
                display: false,
                text: "Months"
              }
            },
            y: {
              beginAtZero: true,
              max: niceMax,      // <-- FIXED
              ticks: {
                callback: (val) => val.toLocaleString("en-IN")
              },
              title: {
                display: true,
                text: "Sales Amount"
              }
            }
          }
        },
      });

    } catch (e) {
      console.error("Target chart error", e);
    }
  }, [targetAchievementReportData, applyTick]);


  /* -------------------------
   Collections (bar)
   ------------------------- */
  useEffect(() => {
    if (!collectionsReportData) return;      // use the actual collections data
    destroyCharts(["collections"]);

    try {
      const ctx = collRef.current.getContext("2d");

      // ✔ 1. Dynamic months from API
      const monthsOrder = Object.keys(collectionsReportData);

      // 2. Convert object data → array in correct order
      const vals = monthsOrder.map(
        (m) => Number(collectionsReportData?.[m] ?? 0)
      );

      // 3. Dynamic scale calculation (with zero-safe handling)
      const rawMax = Math.max(...vals, 0);
      let niceMax;

      if (rawMax === 0) {
        // when everything is 0 → show a clean 0–100 scale
        niceMax = 100;
      } else {
        const maxWithMargin = Math.ceil(rawMax + rawMax * 0.1); // +10%
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxWithMargin)));
        niceMax = Math.ceil(maxWithMargin / magnitude) * magnitude;
      }

      // 4. Draw chart
      charts.current.collections = new Chart(ctx, {
        type: "bar",
        data: {
          labels: monthsOrder,
          datasets: [
            {
              label: "Collections",
              data: vals,
              backgroundColor: palette[2] || palette[0], // pick a color
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "top", align: "end" },
          },
          scales: {
            x: {
              title: {
                display: false,
                text: "2024-25",
              },
            },
            y: {
              beginAtZero: true,
              max: niceMax, // <- apply cleaned max here
              ticks: {
                callback: (val) => val.toLocaleString("en-IN"),
              },
              title: {
                display: true,
                text: "Collection Amount",
              },
            },
          },
        },
      });
    } catch (e) {
      console.error("Collections chart error", e);
    }
  }, [collectionsReportData, applyTick]);



  useEffect(() => () => destroyCharts(), []);

  /* -------------------------
     Fullscreen modal chart creation
     ------------------------- */
  useEffect(() => {
    if (!openModal || !activeChartKey) return;
    setTimeout(() => {
      const canvas = document.getElementById("fullChartCanvas");
      if (!canvas) return;

      if (charts.current.fullscreen) {
        try { charts.current.fullscreen.destroy(); } catch { }
        delete charts.current.fullscreen;
      }

      const original = charts.current[activeChartKey];
      if (!original) return;

      // Fullscreen clone - use _solidColor if present (fallback to backgroundColor / borderColor / palette)
      const clonedData = JSON.parse(JSON.stringify(original.config.data));
      clonedData.datasets = clonedData.datasets.map((ds, i) => {
        const origDs = original.config.data.datasets[i] || {};
        // Prefer explicitly stored serializable color, then backgroundColor, then borderColor, then default palette.
        const solid = origDs._solidColor ?? origDs.backgroundColor ?? origDs.borderColor ?? (palette[i % palette.length]);
        return {
          ...ds,
          backgroundColor: solid,
          borderColor: solid,
          hoverBackgroundColor: solid,
          fill: false,
        };
      });

      try {
        charts.current.fullscreen = new Chart(canvas.getContext("2d"), {
          type: original.config.type,
          data: clonedData,
          options: { ...original.config.options, responsive: true, maintainAspectRatio: false },
        });
      } catch (err) {
        console.error("Fullscreen Chart Error:", err);
      }
    }, 50);
  }, [openModal, activeChartKey]);

  const openFullChart = (chartKey) => {
    setActiveChartKey(chartKey);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    if (charts.current.fullscreen) {
      try { charts.current.fullscreen.destroy(); } catch { }
      delete charts.current.fullscreen;
    }
    setActiveChartKey(null);
  };

 // const noData = !reportData || Object.keys(reportData).length === 0;

  const handleReset = () => {
    setFilters({ party: null });
    if (parties && parties.length > 0) {
      setSelectedParty(parties[0]);
    } else {
      setSelectedParty(null);
    }
    setApplyTick((s) => s + 1);
  };

 
  /* -------------------------
     UI render
     ------------------------- */
  return (
    <Box sx={{ p: 2, backgroundColor: "#ebebebff", minHeight: "100vh", overflow: "hidden", mt: 1, mb: 1 }}>
      {cloading && (
        <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 2000 }}>
          <Paper sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <Loader />
            <Typography>Please wait, fetching data...</Typography>
          </Paper>
        </Box>
      )}

      <Grow in timeout={500}>
        <Typography variant="h4" fontWeight={700} align="center" sx={{ mt: 4, mb: 2, color: "#060606ff", letterSpacing: "0.5px" }}>
          User Reports
        </Typography>
      </Grow>

      {
        user.user_type == "ALL" || user.user_type == "PRI" && (
          <Paper elevation={0} sx={{
            p: 1.5, mb: 1, borderRadius: 2, background: "#fff",
            boxShadow: `0 2px 4px rgba(2,76,174,0.06),0 6px 14px rgba(3,76,172,0.08),inset 0 0 0 1px rgba(47,131,249,0.12)`,
            transform: "scale(0.965)", transformOrigin: "top center", transition: "all 0.25s ease"
          }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1 }}>
                <FilterListIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filters</Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: 1 }}>
                {/* <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" sx={{ color: "#4b5563" }}>Report Type</Typography>
              <Select
                styles={selectStyles(theme)}
                value={selectedReportType}
                onChange={(value) => setSelectedReportType(value)}
                options={reportTypes}
                placeholder="Select Report Type"
              />
            </Box> */}

                {/* <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" sx={{ color: "#4b5563" }}>Category</Typography>
              <Select
                styles={selectStyles(theme)}
                value={selectedCategorie}
                onChange={(value) => setSelectedCategorie(value)}
                options={categories}
                placeholder="Category" />
            </Box> */}

                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="caption" sx={{ color: "#4b5563" }}>
                    Area Sales Manager
                  </Typography>

                  <WindowedSelect
                    options={parties}
                    value={selectedParty}
                    onChange={(value) => setSelectedParty(value)}
                    placeholder="Select Sales Officer"
                    styles={selectStyles(theme)}
                    isSearchable
                    windowThreshold={50}
                    maxHeight={300}
                  />
                </Box>

              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                <Button variant="outlined" startIcon={<FilterListIcon />} sx={{ borderRadius: "10px", px: 3, py: 1, textTransform: "none", fontWeight: 500 }} onClick={handleReset}>Reset</Button>
              </Box>
            </Stack>
          </Paper>
        )
      }

      {/* {noData ? ( */}

        {/* <Paper elevation={1} sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>No data to display</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Try changing filters or refresh the page.</Typography>
        </Paper> */}
        
      {/* ) : ( */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Sales */}
          <Grid item xs={12} sm={6} md={8}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.2, borderRadius: 2, backgroundColor: "#ffffff" }}>
              <CardContent sx={{ pb: 0.2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingUpIcon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Sales
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Last 12 months</Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    {/* Filter Type */}
                    <Box display="flex" alignItems="center" gap={1} width={200}>
                      <Select
                        sx={{ width: "100%" }}
                        options={salesChartTypes}
                        value={selectedSalesChartType}
                        onChange={setSelectedSalesChartType}
                        placeholder="Filter Type"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          container: (base) => ({
                            ...base,
                            width: "100%",
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </Box>

                    {/* Maximize button opens modal for this chart */}
                    <Tooltip title="Maximize" placement="top">
                      <IconButton size="small" onClick={() => openFullChart("sales")} sx={{ backgroundColor: "rgba(0,91,209,0.08)", borderRadius: "10px" }}>
                        <OpenWith sx={{ color: "#1890ff", fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
              <CardContent sx={{ flex: 1, py: 0.5 }}>
                
                {/* <div style={{ height: 280 }}><canvas ref={salesRef} /></div> */}
                <div style={{ height: 280 }}><canvas ref={salesRef} /></div>
              </CardContent>
            </Card>
          </Grid>

          {/* Stock */}
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.2, borderRadius: 2 }}>
              <CardContent sx={{ pb: 0.2 }}>
                <CardHeader title="Stock Levels" subtitle="Current stock by category" Icon={InventoryIcon} chartKey="stock" onMaximize={openFullChart} />
              </CardContent>
              <CardContent sx={{ flex: 1, py: 0.5 }}>
                <div style={{ height: 240 }}><canvas ref={stockRef} /></div>
              </CardContent>
            </Card>
          </Grid>

          {/* Outstanding */}
          <Grid item xs={12} sm={12} md={4}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.2, borderRadius: 2 }}>
              <CardContent sx={{ pb: 0.2 }}>
                <CardHeader title="Outstanding (Aging)" subtitle="Aging buckets" Icon={ReceiptIcon} chartKey="outstanding" onMaximize={openFullChart} />
              </CardContent>
              <CardContent sx={{ flex: 1, py: 0.5 }}>
                <div style={{ height: 160 }}><canvas ref={outRef} /></div>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending vs Dispatch */}
          <Grid item xs={12} sm={6} md={8}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.2, borderRadius: 2 }}>
              <CardContent sx={{ pb: 0.2 }}>
                <CardHeader title="Ordered vs Dispatch" subtitle="Order pipeline" Icon={ShippingIcon} chartKey="pending" onMaximize={openFullChart} />
              </CardContent>
              <CardContent sx={{ flex: 1, py: 0.5 }}>
                <div style={{ height: 160 }}><canvas ref={pendRef} /></div>
              </CardContent>
            </Card>
          </Grid>

          {/* Target */}
          <Grid item xs={12} sm={12} md={6}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.2, borderRadius: 2 }}>
              <CardContent sx={{ pb: 0.2 }}>
                <CardHeader title="Target vs Achievement" subtitle="Per user monthly target" Icon={TargetIcon} chartKey="target" onMaximize={openFullChart} />
              </CardContent>
              <CardContent sx={{ flex: 1, py: 0.5 }}>
                <div style={{ height: 280 }}><canvas ref={targetRef} /></div>
              </CardContent>
            </Card>
          </Grid>

          {/* Collections */}
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.2, borderRadius: 2 }}>
              <CardContent sx={{ pb: 0.2 }}>
                <CardHeader title="Collections" subtitle="Payments received" Icon={CurrencyRupee}  chartKey="collections" onMaximize={openFullChart} />
              </CardContent>
              <CardContent sx={{ flex: 1, py: 0.5 }}>
                <div style={{ height: "100%", minHeight: 220 }}><canvas ref={collRef} /></div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      {/* )} */}

      {/* Fullscreen Modal */}
      <Modal open={openModal} onClose={closeModal}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "90%", height: "90%", p: 3, background: "#fff", borderRadius: 3, outline: "none", display: "flex", flexDirection: "column" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              {(() => {
                const ActiveIcon = activeChartKey
                  ? {
                    sales: TrendingUpIcon,
                    stock: InventoryIcon,
                    outstanding: "ReceiptIcon",
                    pending: ShippingIcon,
                    target: TargetIcon,
                    collections: CurrencyRupee,
                  }[activeChartKey]
                  : null;
                return ActiveIcon ? <ActiveIcon sx={{ fontSize: 26, color: "#005bd1" }} /> : null;
              })()}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{({ sales: "Sales", stock: "Stock Levels", outstanding: "Outstanding (Aging)", pending: "Pending vs Dispatch", target: "Target vs Achievement", collections: "Collections" })[activeChartKey] || "Full View"}</Typography>
              </Box>
            </Box>

            <IconButton onClick={closeModal} sx={{ backgroundColor: "rgba(0,91,209,0.08)", width: 36, height: 36, borderRadius: "10px" }}>
              <CloseIcon sx={{ color: "#005bd1", fontSize: 22 }} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />
          <Box sx={{ flex: 1 }}>
            <canvas id="fullChartCanvas" style={{ width: "100%", height: "100%" }} />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserReports;